import { useState } from 'react';
import { useDebounce } from 'use-debounce';

import {
  useAnnotationQueues,
  useDashboards,
  useDataset,
  useDatasets,
  useHostProject,
  useHostProjects,
  useOrganizationId,
  useRepo,
  useRepos,
  useSession,
  useSessions,
} from '@/hooks/useSwr';
import {
  AnnotationQueueSchema,
  CustomChartsSectionResponse,
  DatasetSchema,
  HostProjectSchema,
  RepoWithLookupsSchema,
  ResourceType,
  SessionSchema,
} from '@/types/schema';

const DEFAULT_LIMIT = 5;
const DEFAULT_OFFSET = 0;

export function useGetResources(resourceType: ResourceType) {
  const [name, setName] = useState('');
  const workspaceId = useOrganizationId();
  const [debounceName] = useDebounce(name, 500, { trailing: true });

  const traces = useSessions(
    resourceType === ResourceType.Project
      ? {
          reference_free: true,
          limit: DEFAULT_LIMIT,
          offset: DEFAULT_OFFSET,
          name_contains: debounceName,
          include_stats: false,
        }
      : null
  );

  const experiments = useSessions(
    resourceType === ResourceType.Experiment
      ? {
          reference_free: false,
          limit: DEFAULT_LIMIT,
          offset: DEFAULT_OFFSET,
          name_contains: debounceName,
          include_stats: false,
        }
      : null
  );

  const datasets = useDatasets(
    resourceType === ResourceType.Dataset
      ? {
          name_contains: debounceName,
          limit: DEFAULT_LIMIT,
          offset: DEFAULT_OFFSET,
        }
      : null
  );

  const deployments = useHostProjects(
    resourceType === ResourceType.Deployment
      ? {
          name_contains: debounceName,
          limit: DEFAULT_LIMIT,
          offset: DEFAULT_OFFSET,
        }
      : null
  );

  const queues = useAnnotationQueues(
    resourceType === ResourceType.Queue
      ? {
          name_contains: debounceName,
          limit: DEFAULT_LIMIT,
          offset: DEFAULT_OFFSET,
        }
      : null
  );

  const prompts = useRepos(
    resourceType === ResourceType.Prompt
      ? {
          query: debounceName,
          limit: DEFAULT_LIMIT,
          offset: DEFAULT_OFFSET,
          tenant_id: workspaceId ?? undefined,
        }
      : null
  );

  const chart_groups = useDashboards(
    resourceType === ResourceType.Dashboard
      ? {
          limit: 100,
          offset: 0,
          title_contains: debounceName,
        }
      : null
  );

  const resource = [
    traces,
    experiments,
    datasets,
    deployments,
    queues,
    prompts,
    chart_groups,
  ].find((r) => r.data);

  let resourceData:
    | DatasetSchema[]
    | HostProjectSchema[]
    | AnnotationQueueSchema[]
    | RepoWithLookupsSchema[]
    | SessionSchema[]
    | CustomChartsSectionResponse[]
    | undefined = undefined;

  // traces and experiements have rows
  if (resource?.data && 'rows' in resource.data && resource.data.rows) {
    resourceData = resource.data.rows;
    // prompts returns repos
  } else if (resource?.data && 'repos' in resource.data) {
    resourceData = resource.data.repos;
    // everything else returns the data
  } else if (Array.isArray(resource?.data)) {
    resourceData = resource.data;
  }

  // default case when loading is not done.
  return {
    resources: resourceData,
    setName,
    name,
    isLoading: resource?.isLoading,
    isError: resource?.error,
  };
}

export const useGetResource = (
  resourceType: ResourceType,
  id: string,
  owner?: string
) => {
  const trace = useSession(resourceType === ResourceType.Project ? id : null);

  const experiment = useSession(
    resourceType === ResourceType.Experiment ? id : null
  );

  const dataset = useDataset(
    resourceType === ResourceType.Dataset ? id : undefined
  );

  const deployment = useHostProject(
    resourceType === ResourceType.Deployment ? id : null
  );

  const queues = useAnnotationQueues(
    resourceType === ResourceType.Queue
      ? {
          ids: [id],
          limit: 1,
          offset: 0,
        }
      : null
  );
  const queue = queues?.data?.[0];

  const prompt = useRepo(
    resourceType === ResourceType.Prompt ? owner : undefined,
    resourceType === ResourceType.Prompt ? id : undefined
  );

  const dashboard = useDashboards(
    resourceType === ResourceType.Dashboard
      ? {
          ids: [id],
          limit: 1,
          offset: 0,
        }
      : null
  );

  let resourceData:
    | DatasetSchema
    | HostProjectSchema
    | AnnotationQueueSchema
    | RepoWithLookupsSchema
    | SessionSchema
    | CustomChartsSectionResponse
    | undefined;
  let isLoading = false;

  switch (resourceType) {
    case ResourceType.Project:
      resourceData = trace.data;
      isLoading = trace.isLoading;
      break;
    case ResourceType.Experiment:
      resourceData = experiment.data;
      isLoading = experiment.isLoading;
      break;
    case ResourceType.Dataset:
      resourceData = dataset.data;
      isLoading = dataset.isLoading;
      break;
    case ResourceType.Deployment:
      resourceData = deployment.data;
      isLoading = deployment.isLoading;
      break;
    case ResourceType.Queue:
      resourceData = queue;
      isLoading = queues.isLoading;
      break;
    case ResourceType.Prompt:
      resourceData = prompt.data?.repo;
      isLoading = prompt.isLoading;
      break;
    case ResourceType.Dashboard:
      resourceData = dashboard?.data?.[0];
      isLoading = dashboard.isLoading;
      break;
  }

  return {
    resource: resourceData,
    name:
      resourceData &&
      ('name' in resourceData
        ? resourceData.name
        : 'title' in resourceData
        ? resourceData.title
        : resourceType === ResourceType.Prompt
        ? id
        : undefined),
    isLoading,
  };
};
